import {TManifestStopEnhanced} from "../../../common/types";
import {getStopAddressText} from "../../../../utils/General";
import {isActiveOrQueued, isBothStop, isDeliveryStop, isPickupStop} from "../../../../utils/stops/StopUtils";
import {TStopLocation} from "./StopLocationSelector";

export const toStopLocation = (stop: TManifestStopEnhanced): TStopLocation => ({
  key: stop.jobStopId,
  address: getStopAddressText({stop, hasDispatchZone: true}),
  stopType: stop.stopType,
  name: stop.name ?? ""
});

// The backend geocodes "address, city, state, zip", so two stops with the same four parts share a location.
const locationKey = (stop: TManifestStopEnhanced) =>
  [stop.address, stop.city, stop.state, stop.zip].map((part) => (part ?? "").trim().toLowerCase()).join("|");

// A job's own stop order (ManifestStop.sequence), as StopUtils.sequenceComparator falls back to.
const byJobSequence = (a: TManifestStopEnhanced, b: TManifestStopEnhanced) => (a.sequence ?? 0) - (b.sequence ?? 0);

/**
 * Stops a dispatcher may pick as "First Stop After Pickup" when the driver starts at pickup `startStopId`
 * (DP-3187 D5, the rule from the ticket). First check whether every open pickup on the manifest is at
 * the start location:
 * - yes (the Medline warehouse case): offer the eligible stops of every job;
 * - no: offer only the active stops of the job the starting pickup belongs to, nothing from other jobs.
 * In both cases the start stop and anything at the start location are excluded. On top of that:
 * - a job with both stops (pickups → both stops in job order → deliveries): with one pickup, only its
 *   first both stop is offered; with several pickups, only its other pickups;
 * - a job with several pickups and one delivery: only its other pickups.
 * Other jobs are not filtered further — GraphHopper routes whatever is picked (D20).
 */
export const getFirstStopAfterPickupOptions = (
  stops: readonly TManifestStopEnhanced[] | null | undefined,
  startStopId: number | undefined
): TStopLocation[] => {
  const openStops = (stops ?? []).filter(isActiveOrQueued);
  const startStop = openStops.find((stop) => stop.jobStopId === startStopId);
  if (!startStop || !isPickupStop(startStop)) return [];

  const startKey = locationKey(startStop);
  const isAtStartLocation = (stop: TManifestStopEnhanced) => locationKey(stop) === startKey;
  const allJobsSharePickup = openStops.filter(isPickupStop).every(isAtStartLocation);
  const startJobId = startStop.job?.jobId;

  return openStops
    .filter((stop) => !isAtStartLocation(stop))
    .filter((stop) => {
      // "no": only the selected pickup's own job
      if (!allJobsSharePickup && stop.job?.jobId !== startJobId) return false;
      const jobStops = openStops.filter((other) => other.job?.jobId === stop.job?.jobId);
      const hasSeveralPickups = jobStops.filter(isPickupStop).length > 1;
      const boths = jobStops.filter(isBothStop).sort(byJobSequence);
      if (boths.length === 0) {
        // several pickups, one delivery: its other pickups only
        return hasSeveralPickups && jobStops.filter(isDeliveryStop).length === 1 ? isPickupStop(stop) : true;
      }
      // a job with both stops: its other pickups while it has several, else its first both stop
      return hasSeveralPickups ? isPickupStop(stop) : stop.jobStopId === boths[0].jobStopId;
    })
    .sort((a, b) => a.manifestSequence - b.manifestSequence)
    .map(toStopLocation);
};
